import React, { useState } from "react";
import { Modal, Space, Select, Typography, Switch, theme} from "antd";
import TextArea from "antd/es/input/TextArea";
import { EInvoiceStatus } from "../../enums";
import InvoicesService from "../../services/http/services/InvoicesService";
import { message as messageAntd } from "antd";

const { Text } = Typography;

interface Props {
  invoice: {
    id: number;
    status: EInvoiceStatus;
    message: string;
  };
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}


const invoiceMessages = {
  [EInvoiceStatus.completed]: [
    "Concluído",
  ],

  [EInvoiceStatus.error]: [
    "Não consta CPF na nota.",
    "Chave da nota inválida.",
    "CPF que consta na nota diferente do CPF cadastrado no usuário.",
    "Nota emitida em contingência e processada após o fechamento do sorteio.",
    "Município selecionado diferente do município de emissão da nota.",
    "Data de emissão da nota não está no período de um sorteio válido.",
    "Nota emitida antes do lançamento do programa.",
    "Período limite de importação para o sorteio atingido.",
    "Quantidade máxima de notas do mês no mesmo estabelecimento, para gerar bilhetes, atingida.",
    "Tipo de nota não participante do programa nota município.",
    "Não consta cidade ou estado na nota.",
  ],

  [EInvoiceStatus.warning]: [
    "Data de emissão da nota não está no período de um sorteio válido.",
    "Período limite de importação para o sorteio atingido.",
    "Nota emitida em contingência e processada após o fechamento do sorteio.",
    "Pontos gerados da nota expiraram.",
  ],

  [EInvoiceStatus.processing]: [
    "Aguardando validação.",
    "Em processamento",
  ],

  [EInvoiceStatus.reprocessing]: [
    "Reprocessando",
  ],
};


export const UpdateInvoiceStatusModal: React.FC<Props> = ({
  invoice,
  open,
  onClose,
  onSuccess,
}) => {

  const { token } = theme.useToken();

  const [status, setStatus] = useState(invoice.status);
  const [message, setMessage] = useState(invoice.message);
  const [selectedMessage, setSelectedMessage] = useState<string>();
  const [deleteTickets, setDeleteTickets] = useState(false);
  const [manuallyValidated, setManuallyValidated] = useState(true);
  const [loading, setLoading] = useState(false);


  const getDefaultMessage = (newStatus: EInvoiceStatus) => {
    return invoiceMessages[newStatus]?.[0] ?? "";
  };


  const handleStatusChange = (newStatus: EInvoiceStatus) => {
    setStatus(newStatus);

    const defaultMessage = getDefaultMessage(newStatus);

    setSelectedMessage(defaultMessage);
    setMessage(defaultMessage);

    if (
      newStatus !== EInvoiceStatus.error &&
      newStatus !== EInvoiceStatus.warning
    ) {
      setDeleteTickets(false);
    }
  };


  const handleSave = async () => {
    try {
      setLoading(true);

      await InvoicesService.updateInvoiceStatus(
        invoice.id,
        {
          status,
          message,
          deleteTickets,
          manuallyValidated
        }
      );

      messageAntd.success("Status da nota atualizado com sucesso!");

      onSuccess?.();
      onClose();

    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      title={`Alterar status da nota #${invoice.id}`}
      open={open}
      onCancel={onClose}
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={loading}
      onOk={handleSave}
    >

      <Space
        direction="vertical"
        size="middle"
        style={{ width: "100%" }}
      >

        <div>
          <Text strong>Status</Text>

          <Select
            style={{
              width: "100%",
              marginTop: 6,
            }}
            value={status}
            onChange={handleStatusChange}
            options={[
              {
                label: "Concluído",
                value: EInvoiceStatus.completed,
              },
              {
                label: "Com erro",
                value: EInvoiceStatus.error,
              },
              {
                label: "Alerta",
                value: EInvoiceStatus.warning,
              },
              {
                label: "Em processamento",
                value: EInvoiceStatus.processing,
              },
              {
                label: "Reprocessando",
                value: EInvoiceStatus.reprocessing,
              },
            ]}
          />
        </div>


        <div>
          <Text strong>
            Mensagem padrão
          </Text>

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Escolha uma mensagem sugerida"
            style={{
              width: "100%",
              marginTop: 6,
            }}
            value={selectedMessage}
            disabled={!invoiceMessages[status]?.length}
            onChange={(value) => {
              setSelectedMessage(value);

              if (value) {
                setMessage(value);
              }
            }}
            options={
              invoiceMessages[status]?.map((msg) => ({
                label: msg,
                value: msg,
              })) ?? []
            }
          />
        </div>


        <div>
          <Text strong>
            Mensagem
          </Text>

          <TextArea
            rows={3}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);

              if (e.target.value !== selectedMessage) {
                setSelectedMessage(undefined);
              }
            }}
          />
        </div>


        {(status === EInvoiceStatus.error ||
          status === EInvoiceStatus.warning) && (

            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: deleteTickets
                  ? token.colorErrorBg
                  : token.colorFillAlter,
                border: `1px solid ${deleteTickets
                  ? token.colorErrorBorder
                  : token.colorBorderSecondary
                  }`,
              }}
            >

              <Space
                align="center"
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >

                <Space>
                  <div>
                    <Text strong>
                      Apagar bilhetes gerados
                    </Text>

                    <br />

                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                      }}
                    >
                      Remove todos os bilhetes vinculados a esta nota
                    </Text>
                  </div>
                </Space>


                <Switch
                  checked={deleteTickets}
                  checkedChildren="Sim"
                  unCheckedChildren="Não"
                  onChange={setDeleteTickets}
                />

              </Space>


            </div>
          )}

        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: manuallyValidated
              ? token.colorSuccessBg
              : token.colorFillAlter,
            border: `1px solid ${manuallyValidated
              ? token.colorSuccessBorder
              : token.colorBorderSecondary
              }`,
          }}
        >

          <Space
            align="center"
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >

            <Space>

              <div>
                <Text strong>
                  Validar nota manualmente
                </Text>

                <br />

                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  Confirma que esta nota foi analisada e considerada válida, mesmo sendo identificada como possível duplicidade.
                </Text>
              </div>
            </Space>


            <Switch
              checked={manuallyValidated}
              checkedChildren="Sim"
              unCheckedChildren="Não"
              onChange={setManuallyValidated}
            />

          </Space>


        </div>

      </Space>

    </Modal>
  );
};